/**
 * @file
 * 查询 event-streaming 接口并拆分常用数据。
 *
 * 支持可选的 data / datas 查询参数：
 * - data: 单个切片，取值 event / question / news / market / social_signal / twitter
 * - datas: 多个切片数组，最终以逗号分隔传给接口；datas 优先于 data
 *
 * 示例：
 * useEventStreaming('14070')
 * useEventStreaming({ eventId: '14070', data: 'news' })
 * useEventStreaming({ eventId: '14070', datas: ['news', 'question'], language: 'zh-CN' })
 */
import type {
  MarketEventFeedItem,
  MarketEventNewsItem,
  MarketEventRelateMarket,
  MarketEventSocialSignal,
} from '@/components/predic/ui/MarketEvent';
import type { TwitterItem } from '@/components/predic/detail/event/TwitterList';
import useSWR, { type SWRConfiguration } from 'swr';

export type EventStreamingDataType =
  | 'event'
  | 'question'
  | 'news'
  | 'market'
  | 'social_signal'
  | 'twitter';

export interface EventStreamingBaseData {
  id: string | number;
  event_id: string;
  title: string;
  recommended_questions: string[];
  summaryGeneratedAt?: string | null;
  description: string | null;
  event_date: string | Date | null;
  image_url: string | null;
  score: number | null;
  importance_score: number | null;
  category: string | null;
  created_at: string | Date | null;
  updated_at: string | Date | null;
  latest_news_time: string | Date | null;
}

export interface EventStreamingData extends EventStreamingBaseData {
  news: MarketEventNewsItem[];
  relate_market: MarketEventRelateMarket[];
  social_signal?: MarketEventSocialSignal | null;
  twitter?: TwitterItem[];
}

export interface EventStreamingQuestionData {
  recommended_questions: string[];
}

export interface EventStreamingNewsData {
  news: MarketEventNewsItem[];
}

export interface EventStreamingMarketData {
  relate_market: MarketEventRelateMarket[];
}

export interface EventStreamingSocialSignalData {
  social_signal?: MarketEventSocialSignal | null;
}

export interface EventStreamingTwitterData {
  twitter: TwitterItem[];
}

export type EventStreamingResponseData<T extends EventStreamingDataType | undefined = undefined> =
  T extends 'event'
    ? EventStreamingBaseData
    : T extends 'question'
      ? EventStreamingQuestionData
      : T extends 'news'
        ? EventStreamingNewsData
        : T extends 'market'
          ? EventStreamingMarketData
          : T extends 'social_signal'
            ? EventStreamingSocialSignalData
            : T extends 'twitter'
              ? EventStreamingTwitterData
              : EventStreamingData;

export type EventStreamingMultiData = Partial<EventStreamingData>;

export interface EventStreamingResponse<TData = EventStreamingData> {
  success: boolean;
  data: TData | null;
  error?: string;
  message?: string;
}

export interface UseEventStreamingOptions<
  T extends EventStreamingDataType | undefined = undefined,
> {
  eventId?: string | number | null;
  data?: T;
  datas?: EventStreamingDataType[];
  language?: string;
  swr?: SWRConfiguration<EventStreamingResponse<EventStreamingResponseData<T>>, Error>;
}

export interface UseEventStreamingResult<T extends EventStreamingDataType | undefined = undefined> {
  data?: EventStreamingResponse<EventStreamingResponseData<T>>;
  result?: EventStreamingResponseData<T>;
  event?: EventStreamingResponseData<T> extends EventStreamingBaseData
    ? EventStreamingResponseData<T>
    : undefined;
  news: MarketEventNewsItem[];
  relateMarket: MarketEventRelateMarket[];
  socialSignal?: MarketEventSocialSignal | null;
  twitter: TwitterItem[];
  recommendedQuestions: string[];
  error?: Error;
  isLoading: boolean;
  isValidating: boolean;
  refresh: () => void;
}

export function useEventStreaming(eventId?: string | number | null): UseEventStreamingResult;
export function useEventStreaming<T extends EventStreamingDataType | undefined = undefined>(
  options?: UseEventStreamingOptions<T>,
): UseEventStreamingResult<T>;
export function useEventStreaming<T extends EventStreamingDataType | undefined = undefined>(
  options: string | number | null | UseEventStreamingOptions<T> = {},
) {
  if (typeof options === 'string' || typeof options === 'number' || options === null) {
    return useEventStreamingWithOptions({ eventId: options });
  }

  return useEventStreamingWithOptions(options);
}

function useEventStreamingWithOptions<T extends EventStreamingDataType | undefined = undefined>(
  options: UseEventStreamingOptions<T> = {},
): UseEventStreamingResult<T> {
  const { eventId, data: dataType, datas, language, swr } = options;
  const searchParams = new URLSearchParams();

  if (eventId !== undefined && eventId !== null && eventId !== '') {
    searchParams.set('eventId', String(eventId));
  }

  const normalizedDatas = normalizeDataTypes(datas);
  if (normalizedDatas.length > 0) {
    searchParams.set('datas', normalizedDatas.join(','));
  } else if (dataType) {
    searchParams.set('data', dataType);
  }

  if (language) searchParams.set('language', language);

  const query = searchParams.toString();
  const swrKey = eventId ? `api:/market/event-streaming?${query}` : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    EventStreamingResponse<EventStreamingResponseData<T>>,
    Error
  >(swrKey, {
    revalidateOnFocus: false,
    ...swr,
  });

  const result = data?.success && data.data ? data.data : undefined;
  const resultRecord =
    result && typeof result === 'object' ? (result as unknown as Record<string, unknown>) : {};

  const news = Array.isArray(resultRecord.news) ? (resultRecord.news as MarketEventNewsItem[]) : [];
  const relateMarket = Array.isArray(resultRecord.relate_market)
    ? (resultRecord.relate_market as MarketEventRelateMarket[])
    : [];
  const twitter = Array.isArray(resultRecord.twitter)
    ? (resultRecord.twitter as TwitterItem[])
    : [];
  const recommendedQuestions = Array.isArray(resultRecord.recommended_questions)
    ? (resultRecord.recommended_questions as string[])
    : [];
  const socialSignal =
    resultRecord.social_signal === undefined
      ? undefined
      : (resultRecord.social_signal as MarketEventSocialSignal | null);
  const event =
    resultRecord.event_id || resultRecord.title
      ? (result as UseEventStreamingResult<T>['event'])
      : undefined;

  return {
    data,
    result,
    event,
    news,
    relateMarket,
    socialSignal,
    twitter,
    recommendedQuestions,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeDataTypes(dataTypes?: EventStreamingDataType[]) {
  if (!dataTypes?.length) return [];
  return [...new Set(dataTypes.filter(Boolean))];
}

export default useEventStreaming;
